// Pure helpers over the product page data: layout fallbacks (phone → tablet →
// web, as the app resolves them), page timing and the background / subtitle geometry ported from the
// old SPA's BookPlayer/utils.js. Coordinates of the authoring tool are in a
// 1000-unit-wide space centred on the image.
import { getImageSize, cleanSrcSet, getFallbackSrc, hasImage } from "../Storefront/utils/images"

const SCENE_TAIL_TIME = 1 // seconds appended to every page
const PLAYER_SAFE_AREA_PERCENT = 5
/**
 * Which layout a page is read in, and what it falls back to.
 *
 * The app resolves one layout and then widens from it — `resolveContentLayout`
 * in aldoo-books book_player/player_state.lua, whose aspect-ratio branch is
 * commented out, so every client reads **phone**. Its `fallbackLayouts` are
 * relative to that choice, not one global order:
 *
 *   phone  → tablet → web
 *   tablet → web    → phone
 *   web    → tablet → phone
 *
 * This matters because a book is authored in the layout the app reads: "The
 * Little Prince" carries all 27 of its page images under `phone` and nothing
 * under `web`, so reading web-first took the empty defaults — a duration of
 * zero and untouched transforms — and left the picture behind.
 */
export const CONTENT_LAYOUTS = ["phone", "tablet", "web"]
const LAYOUT_FALLBACKS = {
  phone: ["tablet", "web"],
  tablet: ["web", "phone"],
  web: ["tablet", "phone"],
}

// What the player reads. The editor moves it to preview another layout; a
// reader never does, which is why this is a module setting and not a prop
// threaded through every helper.
let contentLayout = "phone"
export const setContentLayout = (name) => {
  contentLayout = CONTENT_LAYOUTS.includes(name) ? name : "phone"
  return contentLayout
}
export const getContentLayout = () => contentLayout
export const layoutOrder = () => [contentLayout, ...LAYOUT_FALLBACKS[contentLayout]]

const layoutsOf = (obj) => layoutOrder().map((name) => obj?.[name]).filter(Boolean)

// First layout that has a truthy `field`
const pick = (obj, field) => {
  for (const layout of layoutsOf(obj)) {
    if (layout[field]) return layout[field]
  }
  return undefined
}

export const getPageImage = (page) => {
  for (const layout of layoutsOf(page?.background)) {
    if (hasImage(layout.image)) return layout.image
  }
  return null
}

export const getPageAudio = (page) => pick(page?.subtitle, "audio")

const getPageAudioDuration = (page) => {
  for (const layout of layoutsOf(page?.subtitle)) {
    if (layout.audio) return Number(layout.audioDuration) || 0
  }
  return 0
}

export const getAnimDuration = (page) => Number(pick(page?.background, "duration")) || 0

/** [{start, duration, audioDuration, animDuration}] and the total in seconds */
export function computeTiming(pages = []) {
  let total = 0
  const timing = pages.map((page) => {
    const audioDuration = getPageAudioDuration(page)
    const animDuration = getAnimDuration(page)
    const duration = Math.max(audioDuration, animDuration) + SCENE_TAIL_TIME
    const entry = { start: total, duration, audioDuration, animDuration }
    total += duration
    return entry
  })
  return { timing, total }
}

/** Page index (0-based) that contains the global position (seconds). */
export function pageAt(timing, position) {
  if (!timing.length) return 0
  for (let i = 0; i < timing.length; i++) {
    if (position < timing[i].start + timing[i].duration) return i
  }
  return timing.length - 1
}

function getTransform(background, phase) {
  const field = `${phase}Transform`
  let transform
  for (const layout of layoutsOf(background)) {
    if (layout[field]) {
      transform = layout[field]
      break
    }
  }
  return {
    x: transform?.offsetPos?.x || 0,
    y: transform?.offsetPos?.y || 0,
    scale: transform?.scale || 1,
    angle: transform?.angle || 0,
    verticalFocus: pick(background, "verticalFocus") ?? 0.5,
  }
}

const verticalFocusOffset = (viewport, verticalFocus) => {
  const normalizedHeight = (viewport.height / viewport.width) * 1000
  return (verticalFocus * 2 - 1) * (1000 - normalizedHeight)
}

/**
 * Image rect for a phase ("start" | "end") in viewport pixels:
 * {left, bottom, width, height, angle}. The image is scaled to the viewport
 * width and offset by the authored transform.
 */
export function backgroundRect(viewport, page, phase, size) {
  const image = getPageImage(page)
  const transform = getTransform(page.background, phase)
  // a plain url has no size in it: the caller measures it (useImageSize)
  const { width: imageWidth, height: imageHeight } = size?.width ? size : getImageSize(image)
  if (!imageWidth || !imageHeight || !viewport.width) return null
  const focusOffset = verticalFocusOffset(viewport, transform.verticalFocus)
  const r = viewport.width / imageWidth
  const width = r * imageWidth * transform.scale
  const height = r * imageHeight * transform.scale
  return {
    left: r * transform.x + viewport.width / 2 - width / 2,
    bottom: r * transform.y + viewport.height / 2 - height / 2 + r * focusOffset,
    width,
    height,
    angle: transform.angle,
  }
}

/** Merged subtitle fields for the page (web → tablet → phone per field). */
function getSubtitle(page) {
  const result = { renderBox: {} }
  for (const layout of layoutsOf(page?.subtitle)) {
    for (const key in layout) {
      if (key === "renderBox") continue
      if (result[key] === undefined || result[key] === "" || result[key] === null) result[key] = layout[key]
    }
    for (const key of ["left", "top", "width", "height"]) {
      if (!result.renderBox[key]) result.renderBox[key] = layout.renderBox?.[key]
    }
  }
  return result
}

/**
 * A media value as a url: a plain string, or the first one a per-layout or
 * per-language map holds (a trigger's sound is authored either way).
 */
function firstUrl(value) {
  if (!value) return null
  if (typeof value === "string") return value
  for (const inner of Object.values(value)) {
    const found = firstUrl(inner)
    if (found) return found
  }
  return null
}

/**
 * The tap zones of a page, in viewport pixels.
 *
 * A trigger is authored in the same thousand-wide design space the text is:
 * `area` is {left, top, width, height}, the left measured from the middle of
 * the page and the top from its centre line, so the zones sit on the picture
 * wherever the page is drawn. A tap plays its sound, jumps to a page, or both
 * (aldoo-books book_player/features/playback.lua).
 */
export function triggerRects(viewport, page) {
  const triggers = page?.triggerAreas || []
  if (!triggers.length || !viewport.width) return []
  const transform = getTransform(page.background, "start")
  const focusOffset = verticalFocusOffset(viewport, transform.verticalFocus)
  const scale = viewport.width / 1000

  return triggers
    .map((trigger, index) => {
      const area = trigger?.area
      if (!area) return null
      const width = Math.max(0, (area.width || 0) * scale)
      const height = Math.max(0, (area.height || 0) * scale)
      if (!width || !height) return null
      return {
        index,
        left: (area.left || 0) * scale + viewport.width / 2,
        top: viewport.height / 2 - (area.top || 0) * scale + focusOffset * scale,
        width,
        height,
        audio: firstUrl(trigger.audio),
        gotoPage: Number(trigger.gotoPage) || 0,
      }
    })
    .filter(Boolean)
}

/** Subtitle box positioned in viewport pixels (mirrors subtitlesTransform). */
export function subtitleBox(viewport, page) {
  const subtitle = getSubtitle(page)
  if (!subtitle.text || !viewport.width) return null
  const transform = getTransform(page.background, "start")
  const focusOffset = verticalFocusOffset(viewport, transform.verticalFocus)
  const scale = viewport.width / 1000
  const renderBox = subtitle.renderBox
  const safe = (viewport.height * PLAYER_SAFE_AREA_PERCENT) / 100
  let top = viewport.height / 2 - (renderBox.top || 0) * scale + focusOffset * scale
  top = Math.max(safe, Math.min(top, viewport.height - safe - 40))
  const width = Math.max(120, (renderBox.width || 350) * scale)
  let left = (renderBox.left || 0) * scale + viewport.width / 2
  left = Math.max(8, Math.min(left, viewport.width - width - 8))
  return {
    text: subtitle.text,
    color: subtitle.color || "#000",
    alignment: (subtitle.alignment || "left").toLowerCase(),
    textFrame: subtitle.textFrame,
    fontSize: Math.max(12, (subtitle.fontSize || 18) * scale),
    left,
    top,
    width,
  }
}

/**
 * Attributes for a single <img> from an API image. A plain url stays a single
 * src: the page layout needs its natural size, measured off that same file.
 */
export function imageAttrs(image) {
  if (!image) return null
  if (typeof image === "string") return { src: image }
  const list = image.default || []
  return { src: getFallbackSrc(image), srcSet: cleanSrcSet(list[0]) }
}

/** Playlist entries: chapters when useChapters, otherwise every page. */
export function playlistItems(product, timing) {
  const pages = product?.pages || []
  const useChapters = !!product?.bookSettings?.useChapters
  const items = []
  pages.forEach((page, index) => {
    const isChapter = page.chapterIndex > 0
    if (useChapters && !isChapter) return
    items.push({ index, title: page.title || `${index + 1}`, chapterIndex: page.chapterIndex, duration: timing[index]?.duration || 0 })
  })
  if (!useChapters) return items
  // Chapter duration = its pages until the next chapter
  items.forEach((item, i) => {
    const end = items[i + 1]?.index ?? pages.length
    let sum = 0
    for (let p = item.index; p < end; p++) sum += timing[p]?.duration || 0
    item.duration = sum
  })
  return items
}

/** Index in `items` of the entry that contains the page */
export function activeItemIndex(items, pageIndex) {
  let active = 0
  items.forEach((item, i) => {
    if (item.index <= pageIndex) active = i
  })
  return active
}

export function formatTime(seconds) {
  const s = Math.max(0, Math.floor(Number(seconds) || 0))
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const sec = s % 60
  const mm = h ? String(m).padStart(2, "0") : String(m)
  return `${h ? `${h}:` : ""}${mm}:${String(sec).padStart(2, "0")}`
}
